import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORMS, PLATFORM_LABELS } from './shared/platformLabels.js';
import { nextMondayIsoDate } from './shared/brandFormUtils.js';
import { api } from '../../services/api/client.js';
import { NativeSlider } from './PublishingRulesTab.jsx';
import { useContentFeed } from '../../hooks/useContentFeed.js';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function WeeklyPlanTab({ strategy, platformPrefs = [], platformRegistry = {}, socialAccounts = [], publishingForm, onJumpTo }) {
  const routerNavigate = useNavigate();
  const { startGenerationBatch } = useContentFeed();
  const [weekStart, setWeekStart] = useState(nextMondayIsoDate());
  const [activeWeek, setActiveWeek] = useState(0);
  const [postsToPlan, setPostsToPlan] = useState(Number(publishingForm?.postsPerWeek) || 5);
  const enabledIds = useMemo(
    () => PLATFORMS.filter((p) => platformPrefs.find((row) => row.platform === p)?.enabled),
    [platformPrefs],
  );
  const [picked, setPicked] = useState(enabledIds);
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const [monthBatches, setMonthBatches] = useState([]);
  const [monthBatchesLoading, setMonthBatchesLoading] = useState(false);
  const [monthBatchesError, setMonthBatchesError] = useState(null);
  const [addPostsOpen, setAddPostsOpen] = useState(false);
  const [addPostsCount, setAddPostsCount] = useState(1);
  const [addPostsInstruction, setAddPostsInstruction] = useState('');
  const [addPostsLoading, setAddPostsLoading] = useState(false);
  const monthWeeks = useMemo(() => buildMonthWeeks(weekStart), [weekStart]);
  const monthStartIso = monthWeeks[0]?.iso || weekStart;
  const monthEndIso = addDays(monthWeeks[monthWeeks.length - 1]?.iso || weekStart, 6);
  const weekBatchByIso = useMemo(() => mapWeekBatchesByIso(monthBatches), [monthBatches]);
  const activeWeekBatch = weekBatchByIso.get(monthWeeks[activeWeek]?.iso) || null;
  const scheduleTimezone = publishingForm?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const targetRows = useMemo(
    () => buildTargetRows(socialAccounts, platformRegistry, picked),
    [socialAccounts, platformRegistry, picked.join('|')],
  );
  const readyTargetRows = targetRows.filter((row) => row.ready);
  const readyPlatformSet = new Set(readyTargetRows.map((row) => row.platform));
  const missingTargetPlatforms = picked.filter((id) => !readyPlatformSet.has(id));
  const canScheduleWeek = picked.length > 0 && missingTargetPlatforms.length === 0 && readyTargetRows.length > 0;
  const missingTargetMessage = picked.length && !canScheduleWeek
    ? connectTargetsMessage(missingTargetPlatforms.length ? missingTargetPlatforms : picked, false)
    : null;

  useEffect(() => {
    if (!picked.length && enabledIds.length) setPicked(enabledIds);
  }, [enabledIds.join('|')]);

  useEffect(() => {
    if (!strategy) {
      setMonthBatches([]);
      return undefined;
    }
    let cancelled = false;
    async function loadMonthBatches() {
      try {
        setMonthBatchesLoading(true);
        setMonthBatchesError(null);
        const result = await api.content.listBatches({
          contentPlanWeekly: '1',
          weekStartFrom: monthStartIso,
          weekStartTo: monthEndIso,
          limit: 100,
        });
        if (!cancelled) setMonthBatches(result.batches || []);
      } catch (err) {
        if (!cancelled) {
          setMonthBatches([]);
          setMonthBatchesError(err.message || 'Could not load saved monthly plan weeks.');
        }
      } finally {
        if (!cancelled) setMonthBatchesLoading(false);
      }
    }
    loadMonthBatches();
    return () => {
      cancelled = true;
    };
  }, [strategy, monthStartIso, monthEndIso]);

  const togglePlatform = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openBatch = (batch) => {
    if (batch?.id) routerNavigate(`/posts/create?batch=${encodeURIComponent(batch.id)}`);
  };

  async function addPostsToWeek() {
    if (!activeWeekBatch?.id) return;
    try {
      setAddPostsLoading(true);
      setInlineError(null);
      const result = await api.content.appendBatch(activeWeekBatch.id, {
        count: addPostsCount,
        instruction: addPostsInstruction,
        confirmCreditSpend: true,
      });
      startGenerationBatch?.(result.batch, result.items || []);
      if (result.batch) setMonthBatches((previous) => upsertMonthBatch(previous, result.batch));
      setAddPostsOpen(false);
      setAddPostsInstruction('');
      notify({ level: 'success', title: 'Posts added', description: `${addPostsCount} new post${addPostsCount === 1 ? '' : 's'} will be placed into ${monthWeeks[activeWeek]?.label || 'this week'}.` });
      if (result.batch?.id) openBatch(result.batch);
    } catch (err) {
      if (isCreditError(err)) {
        setInlineError('Plan quota is needed before Dathent can add posts to this week. No quota was used.');
        notify('contentPlan.creditRequired', { error: err });
      } else {
        setInlineError(err.message || 'Unable to add posts to this week.');
        notify('contentPlan.weekFailed', { error: err });
      }
    } finally {
      setAddPostsLoading(false);
    }
  }

  async function generate() {
    if (!strategy) {
      notify('contentPlan.weekMissingStrategy');
      onJumpTo?.('plan');
      return;
    }
    if (!picked.length) {
      notify('contentPlan.weekMissingPlatform');
      return;
    }
    try {
      setLoading(true);
      setInlineError(null);
      const active = monthWeeks[activeWeek] || monthWeeks[0];
      const activeIso = active?.iso || weekStart;
      const scheduleMode = canScheduleWeek ? 'schedule' : 'draft';
      const draftMessage = canScheduleWeek ? null : connectTargetsMessage(missingTargetPlatforms.length ? missingTargetPlatforms : picked, true);
      const result = await api.content.createBatch({
        topic: contentPlanWeekTopic(strategy, active),
        goal: 'Content Plan weekly posts',
        format: 'image_post',
        formats: ['image_post'],
        platforms: picked,
        count: postsToPlan,
        useContentPlan: true,
        contextNote: [
          `Content Plan week starting ${activeIso}`,
          canScheduleWeek ? `Scheduled across the selected week in ${scheduleTimezone}` : draftMessage,
        ].filter(Boolean).join('. '),
        scheduleMode,
        targetAccountIds: canScheduleWeek ? readyTargetRows.map((row) => row.id) : [],
        scheduleStartAt: activeIso,
        scheduleWeekStart: activeIso,
        scheduleTimezone,
        confirmCreditSpend: true,
      });
      startGenerationBatch?.(result.batch, result.items || []);
      if (result.batch) setMonthBatches((previous) => upsertMonthBatch(previous, result.batch));
      if (canScheduleWeek) {
        notify({ level: 'success', title: 'Week scheduled', description: `${postsToPlan} posts planned across ${active?.label || 'the selected week'}.` });
      } else {
        setInlineError(draftMessage);
        notify({ level: 'warning', title: 'Week saved as drafts', description: draftMessage });
      }
      if (result.batch?.id) routerNavigate(`/posts/create?batch=${encodeURIComponent(result.batch.id)}`);
    } catch (err) {
      if (isCreditError(err)) {
        setInlineError('Plan quota is needed before Dathent can generate this week. No quota was used.');
        notify('contentPlan.creditRequired', { error: err });
      } else {
        setInlineError(err.message || 'Unable to start week generation.');
        notify('contentPlan.weekFailed', { error: err });
      }
    } finally {
      setLoading(false);
    }
  }

  if (!strategy) {
    return (
      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">Generate Content Plan first</h3>
          <p className="settings-card-description">Monthly planning needs an active Content Plan first.</p>
        </div>
        <div className="settings-card-footer" style={{ borderTop: 0, paddingTop: 0, marginTop: 0 }}>
          <span />
          <button className="btn btn-primary" onClick={() => onJumpTo?.('plan')}>Open Content Plan</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cp-module-shell">
        <div className="cp-module-hero cp-month-hero">
          <div>
            <div className="settings-section-label">Monthly plan</div>
            <h3>Plan month first. Open weeks only when needed.</h3>
            <p>Each week can be generated from the Content Plan. Empty weeks stay clean placeholders until you add them.</p>
          </div>
          <div className="cp-month-hero-actions">
            {activeWeekBatch && (
              <button className="btn btn-secondary" onClick={() => setAddPostsOpen(true)} disabled={addPostsLoading}>
                <Icon name="plus" size={13} /> Add posts
              </button>
            )}
            <button className="btn btn-primary" onClick={activeWeekBatch ? () => openBatch(activeWeekBatch) : generate} disabled={loading || (!activeWeekBatch && !picked.length)}>
              <Icon name={activeWeekBatch ? 'grid' : 'sparkles'} size={13} /> {activeWeekBatch ? `Open ${monthWeeks[activeWeek]?.label}` : loading ? 'Generating...' : `Generate ${monthWeeks[activeWeek]?.label}`}
            </button>
          </div>
        </div>

        {inlineError && (
          <div className="auth-note" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span className="row" style={{ gap: 8 }}><Icon name="alert" size={14} /> {inlineError}</span>
            {/credit|quota/i.test(inlineError) && <button className="btn btn-secondary btn-sm" onClick={() => routerNavigate('/billing')}>Open billing</button>}
          </div>
        )}

        {missingTargetMessage && !inlineError && (
          <div className="auth-note" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="alert" size={14} />
            <span>{missingTargetMessage}</span>
          </div>
        )}

        {monthBatchesError && (
          <div className="auth-note" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="alert" size={14} />
            <span>{monthBatchesError}</span>
          </div>
        )}

        <div className="cp-form-grid cp-month-controls">
          <Field label="Week start (Monday)">
            <input className="input form-input" type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} />
          </Field>
          <Field label={`Posts in selected week — ${postsToPlan}`}>
            <NativeSlider value={postsToPlan} onChange={setPostsToPlan} min={3} max={14} />
          </Field>
        </div>

        <Field label="Platforms in month plan">
          <div className="cp-chip-toggle-row">
            {PLATFORMS.map((id) => {
              const enabled = enabledIds.includes(id);
              const on = picked.includes(id);
              return (
                <button
                  type="button"
                  key={id}
                  onClick={() => togglePlatform(id)}
                  disabled={!enabled}
                  className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '') + (enabled ? '' : ' is-disabled')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <PlatformIcon platform={id} size={12} withColor={false} />
                  {PLATFORM_LABELS[id]}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="cp-month-board">
          {monthWeeks.map((week, idx) => {
            const batch = weekBatchByIso.get(week.iso) || null;
            const ready = Boolean(batch);
            return (
              <div
                key={week.iso}
                role="button"
                tabIndex={0}
                className={'cp-month-week-card ' + (activeWeek === idx ? 'is-active ' : '') + (ready ? 'is-ready' : '')}
                onClick={() => {
                  setActiveWeek(idx);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveWeek(idx);
                  }
                }}
              >
                <span className="cp-month-week-meta">{week.range}</span>
                <strong>{week.label}</strong>
                <span>{ready ? weekBatchSummary(batch) : monthBatchesLoading ? 'Checking saved weeks...' : 'Not planned yet'}</span>
                <div className="cp-month-week-footer">
                  <em>{ready ? weekBatchStatus(batch) : 'Add week'}</em>
                  {ready && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm cp-month-week-open"
                      onClick={(event) => {
                        event.stopPropagation();
                        openBatch(batch);
                      }}
                    >
                      Open {week.label}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {addPostsOpen && (
        <div className="cp-modal-scrim" role="dialog" aria-modal="true" aria-label="Add posts to weekly plan">
          <div className="card cp-modal cp-add-posts-modal">
            <div className="settings-card-header">
              <div className="settings-section-label">Add to {monthWeeks[activeWeek]?.label || 'week'}</div>
              <h3 className="settings-card-title">Add posts into this week</h3>
              <p className="settings-card-description">Dathent will generate the new posts from your Content Plan and place them into open weekly slots between existing scheduled posts.</p>
            </div>
            <div className="cp-form-grid" style={{ gridTemplateColumns: '1fr', marginTop: 14 }}>
              <Field label={`Posts to add — ${addPostsCount}`}>
                <NativeSlider value={addPostsCount} onChange={setAddPostsCount} min={1} max={6} />
              </Field>
              <Field label="Optional direction, link, or note">
                <textarea
                  className="input form-input"
                  rows={5}
                  value={addPostsInstruction}
                  onChange={(event) => setAddPostsInstruction(event.target.value)}
                  placeholder="Example: add two posts about the new offer and use https://... as context"
                />
              </Field>
            </div>
            <div className="settings-card-footer">
              <button className="btn btn-ghost" onClick={() => setAddPostsOpen(false)} disabled={addPostsLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={addPostsToWeek} disabled={addPostsLoading}>
                <Icon name="sparkles" size={13} /> {addPostsLoading ? 'Adding...' : 'Add to week'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function contentPlanWeekTopic(strategy, week) {
  const inner = strategy?.strategy || {};
  const base = inner.summary || strategy?.summary || inner.positioning || 'Content Plan';
  return `${week?.label || 'Week'}: ${String(base).replace(/\s+/g, ' ').trim().slice(0, 140)}`;
}

function mapWeekBatchesByIso(batches) {
  const byIso = new Map();
  (batches || []).forEach((batch) => {
    const iso = batch.contentPlanWeekStart || contentPlanWeekStartFromNote(batch.contextNote) || localIsoDate(batch.scheduleStartAt, batch.scheduleTimezone);
    if (!iso) return;
    const current = byIso.get(iso);
    if (!current || Date.parse(batch.createdAt || 0) > Date.parse(current.createdAt || 0)) {
      byIso.set(iso, batch);
    }
  });
  return byIso;
}

function upsertMonthBatch(previous, batch) {
  const byId = new Map((previous || []).map((item) => [item.id, item]));
  byId.set(batch.id, { ...byId.get(batch.id), ...batch });
  return Array.from(byId.values());
}

function weekBatchSummary(batch) {
  const count = Number(batch.contentCount ?? batch.totalItems ?? 0);
  const posts = `${Number.isFinite(count) && count > 0 ? count : 1} post${count === 1 ? '' : 's'}`;
  const nextSlot = batch.nextScheduledAt || batch.schedulePlan?.startAt || batch.scheduleStartAt;
  return nextSlot ? `${posts} · Next ${formatScheduleSlot(nextSlot)}` : posts;
}

function weekBatchStatus(batch) {
  if (batch.generatingCount > 0 || ['queued', 'generating', 'reserved'].includes(batch.status)) return 'Generating';
  if (batch.failedCount > 0 && Number(batch.readyCount || 0) === 0) return 'Failed';
  if (batch.scheduleMode && batch.scheduleMode !== 'draft') return 'Scheduled';
  return 'Drafted';
}

function contentPlanWeekStartFromNote(value) {
  return String(value || '').match(/Content Plan week starting (\d{4}-\d{2}-\d{2})/i)?.[1] || null;
}

function localIsoDate(value, timezone = 'UTC') {
  if (!value || Number.isNaN(Date.parse(value))) return null;
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(value)).map((part) => [part.type, part.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    return String(value).slice(0, 10);
  }
}

function formatScheduleSlot(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'planned slot';
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(date);
}

function isCreditError(error) {
  const code = String(error?.code || error?.details?.reason || '').toUpperCase();
  const message = String(error?.message || '');
  return /CREDIT|INSUFFICIENT|QUOTA/.test(code) || /credit|quota/i.test(message);
}

function buildMonthWeeks(firstIso) {
  return Array.from({ length: 4 }).map((_, idx) => {
    const iso = addDays(firstIso, idx * 7);
    return {
      iso,
      label: `Week ${idx + 1}`,
      range: `${iso.slice(5)} - ${addDays(iso, 6).slice(5)}`,
    };
  });
}

function WeekGrid({ plan, weekStart }) {
  const items = collectItems(plan);
  const groups = groupByDay(items, weekStart);

  return (
    <div className="cp-week-grid">
      {DAYS_OF_WEEK.map((label, idx) => {
        const dayDate = addDays(weekStart, idx);
        const dayItems = groups[idx] || [];
        return (
          <div key={label} className="card cp-week-day">
            <div className="cp-week-day-head">
              <span style={{ fontWeight: 600 }}>{label}</span>
              <span className="caption muted" style={{ fontSize: 11 }}>{dayDate.slice(5)}</span>
            </div>
            <div className="cp-week-day-body">
              {dayItems.length === 0 ? (
                <span className="caption muted">No posts</span>
              ) : (
                dayItems.map((item, i) => <PostCard key={i} item={item} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PostCard({ item }) {
  return (
    <div className="cp-week-post">
      <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
        {item.platform && <PlatformIcon platform={item.platform} size={11} />}
        {item.platform && <span className="chip" style={{ padding: '2px 6px', fontSize: 10 }}>{PLATFORM_LABELS[item.platform] || item.platform}</span>}
        {item.format && <span className="chip" style={{ padding: '2px 6px', fontSize: 10 }}>{String(item.format).replaceAll('_', ' ')}</span>}
      </div>
      <div className="cp-week-post-body">{item.angle || item.topic || item.title || 'Post'}</div>
      {item.time && <div className="caption muted" style={{ fontSize: 11, marginTop: 4 }}>{item.time}</div>}
    </div>
  );
}

function collectItems(plan) {
  if (Array.isArray(plan)) return plan;
  if (Array.isArray(plan?.items)) return plan.items;
  if (Array.isArray(plan?.posts)) return plan.posts;
  if (Array.isArray(plan?.weeklyPlan)) return plan.weeklyPlan;
  if (Array.isArray(plan?.days)) {
    return plan.days.flatMap((d) => (d.posts || []).map((p) => ({ ...p, day: d.day || d.date, time: p.time })));
  }
  return [];
}

function groupByDay(items, weekStart) {
  const map = {};
  items.forEach((raw, idx) => {
    const item = normalizeItem(raw);
    const dayIdx = inferDayIndex(item, weekStart, idx, items.length);
    if (!map[dayIdx]) map[dayIdx] = [];
    map[dayIdx].push(item);
  });
  return map;
}

function normalizeItem(raw) {
  return {
    platform: raw.platform || raw.channel,
    format: raw.format || raw.type,
    angle: raw.angle || raw.summary || raw.title || raw.topic,
    topic: raw.topic,
    time: raw.time || raw.scheduledAt || raw.timeSlot,
    day: raw.day || raw.date,
  };
}

function inferDayIndex(item, weekStart, idx, total) {
  if (typeof item.day === 'number') return Math.max(0, Math.min(6, item.day));
  if (typeof item.day === 'string') {
    const lower = item.day.toLowerCase();
    const names = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const named = names.findIndex((n) => lower.startsWith(n.slice(0, 3)));
    if (named >= 0) return named;
    if (/^\d{4}-\d{2}-\d{2}/.test(lower)) {
      const ms = Date.parse(lower) - Date.parse(weekStart);
      const days = Math.floor(ms / 86_400_000);
      return Math.max(0, Math.min(6, days));
    }
  }
  return Math.floor((idx / Math.max(1, total)) * 7) % 7;
}

function addDays(iso, n) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function buildTargetRows(accounts, registry, platforms) {
  const selected = new Set(platforms.map(normalizePlatform).filter(Boolean));
  return (accounts || [])
    .map((account) => {
      const platform = normalizePlatform(account.platform);
      if (!selected.has(platform)) return null;
      const blockedReason = publishTargetBlockedReason(account, registry || {});
      return { id: account.id, platform, ready: !blockedReason, blockedReason };
    })
    .filter(Boolean);
}

function publishTargetBlockedReason(account, registry) {
  const platform = normalizePlatform(account.platform);
  if (account.status !== 'connected') return 'Reconnect required before publishing';
  if (registry[platform] && !registry[platform].supportsPublish) return 'Publishing not enabled for this platform';
  if (platform === 'telegram' && account.capabilities?.destinationType && account.capabilities.destinationType !== 'channel') {
    return 'Telegram publishing requires a connected channel';
  }
  return null;
}

function connectTargetsMessage(platforms, past) {
  const labels = platforms.map((id) => PLATFORM_LABELS[id] || id).join(', ');
  return `Connect publish-ready ${labels} target${platforms.length === 1 ? '' : 's'} to schedule automatically. This week ${past ? 'was' : 'will be'} generated as drafts until those targets are ready.`;
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function Field({ label, hint, children }) {
  return (
    <div className="form-field cp-field">
      <label>{label}</label>
      {children}
      {hint && <div className="helper">{hint}</div>}
    </div>
  );
}
